import { Link, useParams } from 'react-router-dom';
import { useBlock } from '../hooks/useBlock';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { ApiError } from '../api/client';
import { formatNumber, formatTimestamp } from '../util/format';
import { UtxoBlockHeader } from '../components/UtxoBlockHeader';
import { NevmSummaryCard } from '../components/NevmSummaryCard';
import { MinerBadge } from '../components/MinerBadge';

export const UtxoBlockPage = () => {
  const { blockHash } = useParams<{ blockHash: string }>();
  const { data, loading, error, refetch } = useBlock(blockHash);

  if (!blockHash) {
    return <ErrorMessage message="Invalid UTXO block hash" />;
  }

  if (loading) {
    return <LoadingSpinner message="Loading UTXO block data..." />;
  }

  if (error) {
    if (error instanceof ApiError && error.status === 404) {
      return (
        <div className="info-item">
          <p>
            Block not indexed yet. It has been queued for indexing. Please try
            again shortly.
          </p>
          <button className="load-more-button" onClick={refetch}>
            Check Again
          </button>
        </div>
      );
    }
    return <ErrorMessage error={error} retry={refetch} />;
  }

  if (!data) {
    return <ErrorMessage message="Block not found" />;
  }

  const {
    utxo_block: block,
    chainlock_finality: chainlockFinality,
    block_time_delta: blockTimeDelta,
    nevm: nevmData,
    auxpow_anchor: auxpowAnchor,
    syscoin_commitments: syscoinCommitments,
  } = data;

  const auxpowMiner = block.auxpow_miner;
  const auxpowMinerMempoolId = block.auxpow_miner_mempool_id;

  return (
    <div className="block-info">
      <UtxoBlockHeader height={block.height} hash={block.hash} confirmations={block.confirmations} />

      <div className="info-grid">
        <div className="info-item">
          <strong>Block Hash:</strong>
          <code className="hash-display">{block.hash}</code>
        </div>
        <div className="info-item">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              fontSize: '0.9em',
            }}
          >
            {blockTimeDelta ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <span
                  style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}
                >
                  Block Time:
                </span>
                <span>
                  {(blockTimeDelta / 60).toFixed(2)} min ({blockTimeDelta}s){' '}
                  {blockTimeDelta > 300 ? (
                    <span style={{ color: '#FFC107' }}>🐢 Slow</span>
                  ) : blockTimeDelta > 240 ? (
                    <span style={{ color: '#FF9800' }}>⏱️ Delayed</span>
                  ) : blockTimeDelta < 60 ? (
                    <span style={{ color: '#81C784' }}>⚡ Fast</span>
                  ) : (
                    <span style={{ color: '#4CAF50' }}>✓ Normal</span>
                  )}
                </span>
              </div>
            ) : null}

            {block.difficulty ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <span
                  style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}
                >
                  Difficulty:
                </span>
                <span>{block.difficulty}</span>
              </div>
            ) : null}

            {block.bits ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <span
                  style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}
                >
                  Bits:
                </span>
                <span>{block.bits}</span>
              </div>
            ) : null}
          </div>
        </div>
        <div className="info-item">
          <strong>Previous Block:</strong>
          {block.previousblockhash ? (
            <Link
              to={`/utxo/block/${block.previousblockhash}`}
              style={{ textDecoration: 'none' }}
            >
              <code className="hash-display hash-link">
                {block.previousblockhash}
              </code>
            </Link>
          ) : (
            <span style={{ fontSize: '0.85em' }}>N/A</span>
          )}
        </div>
        <div className="info-item">
          <strong>Merkle Root:</strong>
          <code className="hash-display">{block.merkleroot}</code>
        </div>
        <div className="info-item">
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              fontSize: '0.9em',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              <span
                style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}
              >
                Size:
              </span>
              <span>{formatNumber(block.size)} bytes</span>
            </div>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '8px',
              }}
            >
              <span
                style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}
              >
                Nonce:
              </span>
              <span>{block.nonce}</span>
            </div>
            {block.versionHex ? (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                }}
              >
                <span
                  style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}
                >
                  Version:
                </span>
                <span>{block.versionHex}</span>
              </div>
            ) : null}
          </div>
        </div>
        {auxpowMiner ? (
          <div className="info-item">
            <MinerBadge
              name={auxpowMiner}
              mempoolId={auxpowMinerMempoolId}
              nameColor="#81C784"
              minedOn={formatTimestamp(block.time)}
            />
          </div>
        ) : null}
      </div>

      <h2 style={{ marginTop: '30px' }}>Finality</h2>
      <div
        className="navigation-card"
        style={{
          borderColor: chainlockFinality ? '#4CAF50' : '#FFC107',
        }}
      >
        {chainlockFinality ? (
          <>
            <p>
              <strong>ChainLock:</strong>{' '}
              <span style={{ color: '#4CAF50' }}>✓ FINAL</span>
            </p>
            <p
              style={{
                marginTop: '10px',
                color: '#888',
                fontSize: '0.95em',
              }}
            >
              This block is ChainLock-final because it is at or below the
              latest ChainLock frontier (H_CL). ChainLocks are produced by the
              sentrynode quorum with BLS signatures, and a block becomes final
              as soon as it’s inside the finalized prefix covered by the latest
              ChainLock.
            </p>
          </>
        ) : (
          <>
            <p>
              <strong>ChainLock:</strong>{' '}
              <span style={{ color: '#FFC107' }}>Not final</span>
            </p>
            <p
              style={{
                marginTop: '10px',
                color: '#888',
                fontSize: '0.95em',
              }}
            >
              ChainLocks are created by Syscoin&apos;s sentrynode quorum which
              signs blocks using BLS signatures. This block is not yet inside
              the finalized prefix covered by the latest ChainLock.
            </p>
          </>
        )}

        <p style={{ marginTop: '15px' }}>
          <strong>Confirmations:</strong> {formatNumber(block.confirmations)}
        </p>
        <p
          style={{
            marginTop: '10px',
            color: '#888',
            fontSize: '0.95em',
          }}
        >
          {chainlockFinality
            ? 'This block is already chainlock final. Confirmations provide additional security through Nakamoto consensus.'
            : 'For blocks without chainlock finality, Nakamoto consensus rules apply. The block safety increases with each confirmation (subsequent block mined on top).'}
        </p>
      </div>

      <h2 style={{ marginTop: '30px' }}>AuxPoW (Merge-Mining)</h2>
      <div
        className="navigation-card"
        style={{
          borderColor:
            auxpowAnchor && auxpowAnchor.has_auxpow ? '#4CAF50' : '#666',
        }}
      >
        {auxpowAnchor && auxpowAnchor.has_auxpow && auxpowAnchor.btc_block_hash ? (
          <>
            <p style={{ marginBottom: '15px' }}>
              {auxpowAnchor.indexed
                ? 'This UTXO block was merge-mined in the following Bitcoin block.'
                : 'This UTXO block contains merge-mining proof with the following hash.'}
            </p>

            <div className="info-grid">
              <div className="info-item" style={{ gridColumn: 'span 2' }}>
                <strong>
                  {auxpowAnchor.indexed
                    ? 'Bitcoin Block Hash:'
                    : 'Merge-Mining Hash:'}
                </strong>
                <code className="hash-display">
                  {auxpowAnchor.btc_block_hash}
                </code>
                {auxpowAnchor.indexed && (
                  <div
                    style={{
                      marginTop: '12px',
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '16px',
                      fontSize: '0.9em',
                    }}
                  >
                    <span>
                      <strong style={{ color: '#8b92a8' }}>Height:</strong>{' '}
                      {formatNumber(auxpowAnchor.btc_block_height || 0)}
                    </span>
                    <span>
                      <strong style={{ color: '#8b92a8' }}>Timestamp:</strong>{' '}
                      {auxpowAnchor.timestamp
                        ? formatTimestamp(auxpowAnchor.timestamp)
                        : 'N/A'}
                    </span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <Link
                  to={`/auxpow/${auxpowAnchor.btc_block_hash}`}
                  className="nav-button"
                >
                  {auxpowAnchor.indexed ? 'View details →' : 'View AuxPow details'}
                </Link>
                {auxpowAnchor.indexed && (
                  <a
                    href={`https://mempool.space/block/${auxpowAnchor.btc_block_hash}`}
                    className="nav-button"
                    target="_blank"
                    rel="noopener"
                  >
                    View on mempool.space ↗
                  </a>
                )}
              </div>
            </div>
          </>
        ) : (
          <p style={{ color: '#666' }}>
            <em>No AuxPoW data available for this block.</em>
          </p>
        )}
      </div>

      {syscoinCommitments && syscoinCommitments.length > 0 && (
        <>
          <h2 style={{ marginTop: '30px' }}>Syscoin Commitments</h2>
          <p className="subtitle" style={{ marginTop: '4px', marginBottom: '16px' }}>
            Bitcoin blocks that include this UTXO block hash in their coinbase transaction
          </p>
          {syscoinCommitments.map((commitment, idx) => (
            <div
              key={`${commitment.btc_block_hash}-${idx}`}
              className="navigation-card"
              style={{ borderColor: '#f7931a', marginBottom: '16px' }}
            >

              <div className="info-grid">
                <div className="info-item">
                  <strong>Bitcoin Block Hash:</strong>
                  <code className="hash-display">
                    {commitment.btc_block_hash}
                  </code>
                </div>
                <div className="info-item" style={{ fontSize: '0.9em' }}>
                  {commitment.miner_mempool_id ? (
                    <a
                      href={`https://mempool.space/mining/pool/${commitment.miner_mempool_id}`}
                      target="_blank"
                      rel="noopener"
                      style={{
                        textDecoration: 'none',
                        display: 'block',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'center',
                          marginBottom: '10px',
                        }}
                      >
                        <div style={{ fontSize: '2em', lineHeight: 1 }}>⛏️</div>
                        <div>
                          <div
                            style={{
                              fontWeight: 'bold',
                              color: '#f7931a',
                            }}
                          >
                            {commitment.miner || 'Unknown'}
                          </div>
                          <div
                            style={{
                              color: '#8b92a8',
                              marginTop: '2px',
                            }}
                          >
                            Bitcoin Mining Pool
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          color: '#8b92a8',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                        }}
                      >
                        <span style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}>
                          Bitcoin block:
                        </span>
                        <span>
                          #{formatNumber(commitment.btc_block_height)}
                          {commitment.btc_depth != null && (
                            <span
                              style={{
                                color:
                                  commitment.btc_depth >= 6
                                    ? '#4CAF50'
                                    : '#FFC107',
                                marginLeft: '6px',
                              }}
                            >
                              ▪ {formatNumber(commitment.btc_depth)} confirmation{commitment.btc_depth === 1 ? '' : 's'}
                            </span>
                          )}
                        </span>
                      </div>
                      <div
                        style={{
                          color: '#8b92a8',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                        }}
                      >
                        <span style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}>
                          UTXO block:
                        </span>
                        <span>
                          #{formatNumber(block.height)}
                          <span
                            style={{
                              color:
                                block.confirmations >= 6
                                  ? '#4CAF50'
                                  : '#FFC107',
                              marginLeft: '6px',
                            }}
                          >
                            ▪ {formatNumber(block.confirmations)} confirmation{block.confirmations === 1 ? '' : 's'}
                          </span>
                        </span>
                      </div>
                    </a>
                  ) : (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          alignItems: 'center',
                          marginBottom: '10px',
                        }}
                      >
                        <div style={{ fontSize: '2em', lineHeight: 1 }}>⛏️</div>
                        <div>
                          <div
                            style={{
                              fontWeight: 'bold',
                              color: commitment.miner ? '#f7931a' : '#8b92a8',
                            }}
                          >
                            {commitment.miner || 'Unknown'}
                          </div>
                          <div
                            style={{
                              color: '#8b92a8',
                              marginTop: '2px',
                            }}
                          >
                            Bitcoin Mining Pool
                          </div>
                        </div>
                      </div>
                      <div
                        style={{
                          color: '#8b92a8',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                        }}
                      >
                        <span style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}>
                          Bitcoin block:
                        </span>
                        <span>
                          #{formatNumber(commitment.btc_block_height)}
                          {commitment.btc_depth != null && (
                            <span
                              style={{
                                color:
                                  commitment.btc_depth >= 6
                                    ? '#4CAF50'
                                    : '#FFC107',
                                marginLeft: '6px',
                              }}
                            >
                              ▪ {formatNumber(commitment.btc_depth)} confirmation{commitment.btc_depth === 1 ? '' : 's'}
                            </span>
                          )}
                        </span>
                      </div>
                      <div
                        style={{
                          color: '#8b92a8',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '6px',
                        }}
                      >
                        <span style={{ color: '#8b92a8', whiteSpace: 'nowrap' }}>
                          UTXO block:
                        </span>
                        <span>
                          #{formatNumber(block.height)}
                          <span
                            style={{
                              color:
                                block.confirmations >= 6
                                  ? '#4CAF50'
                                  : '#FFC107',
                              marginLeft: '6px',
                            }}
                          >
                            ▪ {formatNumber(block.confirmations)} confirmation{block.confirmations === 1 ? '' : 's'}
                          </span>
                        </span>
                      </div>
                    </>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Link
                    to={`/auxpow/${commitment.btc_block_hash}?utxo=${block.hash}`}
                    className="nav-button"
                  >
                    View details →
                  </Link>
                  <a
                    href={`https://mempool.space/block/${commitment.btc_block_hash}`}
                    className="nav-button"
                    target="_blank"
                    rel="noopener"
                  >
                    View on mempool.space ↗
                  </a>
                </div>
              </div>
            </div>
          ))}
        </>
      )}

      {nevmData && <NevmSummaryCard nevm={nevmData} />}
    </div>
  );
};
